import { campaignReviewDates } from "@/data/campaignReview";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { articles } from "@/mocks/carriers";
import { legacyArticles } from "@/data/legacyArticles.generated";
import BillBasicsArticle from "@/components/articles/BillBasicsArticle";
import { billBasicsToc } from "@/components/articles/billBasicsToc";
import CarrierEditorialArticle from "@/components/articles/CarrierEditorialArticles";
import GuideEditorialArticle from "@/components/articles/GuideEditorialArticles";
import ReturnProgramArticle from "@/components/articles/ReturnProgramArticle";
import Iphone18ProMnpArticle from "@/components/articles/Iphone18ProMnpArticle";
import WhichPlanIsCheapestArticle from "@/components/articles/WhichPlanIsCheapestArticle";
import AreYouOverpayingArticle from "@/components/articles/AreYouOverpayingArticle";
import { IijmioGuideArticle, IijmioIphoneCaseArticle } from "@/components/articles/IijmioArticles";
import YmobileArticle from "@/components/articles/YmobileArticle";
import UqmobileArticle from "@/components/articles/UqmobileArticle";
import SimMnpCampaignArticle from "@/components/articles/SimMnpCampaignArticle";
import RakutenIphone18ProArticle from "@/components/articles/RakutenIphone18ProArticle";
import RakutenIphone17eArticle from "@/components/articles/RakutenIphone17eArticle";
import { editorialTocs } from "@/components/articles/editorialTocs";
import authorProfile from "@/assets/author-profile.webp";

function ArticleBody({ id }: { id: string }) {
  if (id === "rakuten-iphone-17e") return <RakutenIphone17eArticle />;
  if (id === "rakuten-iphone-18-pro") return <RakutenIphone18ProArticle />;
  if (id === "sim-only-mnp-campaigns") return <SimMnpCampaignArticle />;
  if (id === "are-you-overpaying") return <AreYouOverpayingArticle />;
  if (id === "which-plan-is-cheapest") return <WhichPlanIsCheapestArticle />;
  if (id === "return-program") return <ReturnProgramArticle />;
  if (id === "iphone-18-pro-mnp") return <Iphone18ProMnpArticle />;
  if (id === "bill-basics") return <BillBasicsArticle />;
  if (id === "iijmio-guide") return <IijmioGuideArticle />;
  if (id === "iijmio-iphone-no-return") return <IijmioIphoneCaseArticle />;
  if (id === "ymobile-guide") return <YmobileArticle />;
  if (id === "uqmobile-guide") return <UqmobileArticle />;
  if (id === "ahamo-guide" || id === "rakuten-guide") return <CarrierEditorialArticle id={id} />;
  if (
    id === "before-switching" || id === "data-usage" || id === "iphone-buy" ||
    id === "iphone-repair-or-replace" || id === "used-iphone-checklist"
  ) return <GuideEditorialArticle id={id} />;
  return null;
}

export default function ArticleDetail() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id) || articles[0];
  const legacyArticle = legacyArticles.find((item) => item.id === article.id);
  const articleToc = article.id === "bill-basics" ? billBasicsToc : editorialTocs[article.id] || legacyArticle?.toc || [];
  const relatedIds = article.id === "sim-only-mnp-campaigns"
    ? ["iphone-18-pro-mnp", "which-plan-is-cheapest", "ahamo-guide"]
    : article.id === "iphone-18-pro-mnp"
    ? ["return-program", "sim-only-mnp-campaigns", "iphone-buy"]
    : article.id === "return-program"
      ? ["iphone-18-pro-mnp", "iphone-buy", "which-plan-is-cheapest"]
      : [];
  const related = relatedIds.length
    ? relatedIds.map((relatedId) => articles.find((item) => item.id === relatedId)).filter((item): item is (typeof articles)[number] => Boolean(item))
    : articles.filter((item) => item.id !== article.id).slice(0, 3);
  const publishedDate = article.date.replaceAll(".", "-");

  return (
    <Layout>
      <article>
        <div className="mx-auto max-w-[820px] px-4 md:px-6">
          <div className="pt-8 md:pt-10">
            <Link to="/articles" className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600">
              <i className="ri-arrow-left-line" aria-hidden="true" />
              記事一覧へ戻る
            </Link>
          </div>
          <header className="pb-8 pt-6 text-center md:pb-10 md:pt-8">
            {(article.id === "sim-only-mnp-campaigns" || article.id === "ahamo-guide" || article.id === "rakuten-guide" || article.id === "rakuten-iphone-18-pro" || article.id === "rakuten-iphone-17e") && <p className="mx-auto mb-4 w-fit border border-primary-600 px-3 py-1 text-xs font-bold text-primary-700">PR｜この記事には広告が含まれます</p>}
            <div className="text-xs font-semibold text-primary-600">{article.category}</div>
            <h1 className="font-editorial mt-4 text-2xl font-bold leading-snug text-foreground-900 md:text-[34px]">{article.title}</h1>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-foreground-500">
              <time dateTime={publishedDate}>公開 {article.date}</time>
              <span className="h-1 w-1 rounded-full bg-foreground-300" aria-hidden="true" />
              <span>読了 {article.readTime}</span>
            </div>
          </header>

          <div className="mb-8 grid gap-4 border-y border-background-300 bg-white py-5 text-left sm:grid-cols-[64px_1fr] md:grid-cols-[64px_1fr_auto] md:items-center md:gap-6">
            <img
              src={authorProfile}
              alt="スマートフォンを確認する記事執筆者のプロフィール画像"
              width="64"
              height="64"
              className="h-16 w-16 rounded-full border border-background-300 bg-background-100 object-cover object-top"
            />
            <div>
              <div className="text-xs font-bold tracking-wider text-primary-600">執筆・内容確認</div>
              <div className="mt-1 text-base font-bold text-foreground-900">スマホ代見直し案内所 編集部</div>
              <p className="mt-1 text-xs leading-6 text-foreground-600">
                元キャリアショップ店長・通信業界25年のプロが、公式情報を確認して分かりやすく解説します。
              </p>
            </div>
            <div className="text-xs leading-6 text-foreground-500 sm:col-start-2 md:col-start-auto md:max-w-[230px]">
              <div><time dateTime={publishedDate}>内容確認：{article.date}</time></div>
              {campaignReviewDates[article.id] && <div>キャンペーン再確認：{campaignReviewDates[article.id]}</div>}
              <Link to="/policy" className="font-semibold text-primary-600 hover:underline">比較基準・広告方針を見る →</Link>
            </div>
          </div>

        </div>

        {article.id !== "rakuten-iphone-18-pro" && article.id !== "rakuten-iphone-17e" && <div className="mx-auto max-w-[960px] px-4 md:px-6">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img src={article.image} alt={article.title} className="h-[300px] w-full object-cover object-top md:h-[440px]" />
          </div>
        </div>}

        <div className="mx-auto max-w-[820px] px-4 pt-10 md:px-6 md:pt-12">
          {article.id === "rakuten-iphone-17e" && <section id="answer" className="article-body mb-10">
            <p>iPhoneは欲しい。でも、最近の価格を見ると手が出しにくい。そんな人に検討してほしいのが、楽天モバイルのiPhone 17eです。</p>
            <p>iPhone 17e 256GBは、楽天モバイルへ他社から乗り換え、対象プランと同時に楽天カード48回払いで買うと、<strong>最初の24回は端末代が月1円</strong>です。通信料を足しても、データ3GBまでなら月1,079円、20GB超なら月3,279円になります。</p>
            <p>ただし、月1円のまま買い切れるわけではありません。約2年で返す場合と、返さず使い続ける場合の総額、機種変更の人の価格を分けて確認します。</p>
            <p className="text-sm text-foreground-600">※金額は税込・通信料の割引前。iPhone 17e 256GBを買い替え超トクプログラムで購入した場合です。ポイント還元は含めていません。</p>
          </section>}
          {article.id === "rakuten-iphone-18-pro" && <section id="answer" className="article-body mb-10">
            <p>iPhone 18 Proを楽天モバイルで買うと、買い替え超トクプログラム利用時の<strong>毎月の支払いは端末代5,331円＋通信料</strong>です。データを20GB超使っても通信料は月3,278円なので、合計は<strong>月8,609円</strong>になります。</p>
            <p>端末を約2年で返すか、最後まで払い終えるかで、端末代の総額は変わります。この記事では毎月の支払い、返却する場合の費用、楽天モバイルが向いている人と注意点を順番に確認します。</p>
            <p className="text-sm text-foreground-600">※金額は税込・割引前。iPhone 18 Pro 256GBを楽天カードの48回払いで購入した場合です。ポイント還元は含めていません。</p>
          </section>}
          {articleToc.length > 0 && (
            <nav className="mb-10 rounded-lg border border-background-300 bg-background-50 p-5 md:p-6">
              <div className="text-xs font-bold tracking-wider text-foreground-700">目次</div>
              <ol className="mt-3 space-y-2 text-sm text-foreground-800">
                {articleToc.map((item, index) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} className="flex cursor-pointer items-start gap-3 hover:text-primary-600">
                      <span className="w-5 shrink-0 pt-0.5 text-xs font-semibold text-foreground-400">{index + 1}.</span>
                      <span className="leading-6">{item.label}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          <div className="article-body"><ArticleBody id={article.id} /></div>

          <p className="mt-10 border-t border-background-200 pt-4 text-xs leading-6 text-foreground-500">
            {article.id === "rakuten-iphone-18-pro" || article.id === "rakuten-iphone-17e" || article.id === "rakuten-guide"
              ? <>この記事の楽天モバイルの申込リンクには成果報酬型広告を使用しています。キャンペーン・端末購入のリンクは公式サイトへの直接リンクです。申込み前に最新条件をご確認ください。広告計測については<Link to="/privacy" className="ml-1 font-semibold text-primary-600 hover:underline">プライバシーポリシー</Link>をご覧ください。</>
              : article.id === "sim-only-mnp-campaigns" || article.id === "ahamo-guide"
              ? <>この記事のahamoのSIMのみ申込リンクには成果報酬型広告を使用しています。端末購入など、ほかの申込リンクは公式サイトへの直接リンクです。申込み前にリンク先の最新条件をご確認ください。広告計測については<Link to="/privacy" className="ml-1 font-semibold text-primary-600 hover:underline">プライバシーポリシー</Link>をご覧ください。</>
              : "現在、この記事内の申込み先リンクは各社公式サイトへの直接リンクです。今後、成果報酬型広告を掲載する場合は、広告リンクの近くに「PR」等を表示します。申込み前にリンク先の最新条件をご確認ください。"}
          </p>

          <div className="mt-16 border-t border-background-300 pb-16 pt-10">
            <div className="font-editorial text-xl font-bold text-foreground-900">関連記事</div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {related.map((item) => (
                <Link key={item.id} to={`/articles/${item.id}`} className="group cursor-pointer">
                  <div className="flex h-full flex-col overflow-hidden rounded-lg border border-background-300 bg-background-50">
                    <div className="aspect-[16/10] overflow-hidden bg-background-200">
                      <img src={item.image} alt={item.title} className="h-full w-full object-cover object-top transition-transform group-hover:scale-[1.03]" />
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <div className="text-[11px] font-semibold text-primary-600">{item.category}</div>
                      <div className="mt-1.5 text-[14px] font-bold leading-6 text-foreground-900 group-hover:text-primary-600">{item.title}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}
